"use client";

/**
 * Helpers for content documents stored as book_spreads units.
 * A unit is either:
 * - single: one physical page
 * - spread: two physical pages sharing one coordinate space
 */

export function normalizeContentLayout(value: any): "book" | "spreads_only" {
  return value === "spreads_only" ? "spreads_only" : "book";
}

export const DEFAULT_PAGE_MARGINS_MM = {
  top: 12,
  bottom: 12,
  inner: 15,
  outer: 12,
};

export function normalizePageMarginsMm(margins: any) {
  return {
    top: Number(margins?.top ?? DEFAULT_PAGE_MARGINS_MM.top),
    bottom: Number(margins?.bottom ?? DEFAULT_PAGE_MARGINS_MM.bottom),
    inner: Number(margins?.inner ?? DEFAULT_PAGE_MARGINS_MM.inner),
    outer: Number(margins?.outer ?? DEFAULT_PAGE_MARGINS_MM.outer),
  };
}

// Compute book-like text margin boxes for current unit (single/spread).
export function getMarginBoxesMm({
  trimWmm,
  trimHmm,
  margins,
  isSpread
}: {
  trimWmm: number;
  trimHmm: number;
  margins: { top: number; bottom: number; inner: number; outer: number };
  isSpread: boolean;
}) {
  const normalized = normalizePageMarginsMm(margins);
  const top = normalized.top;
  const bottom = normalized.bottom;
  const inner = normalized.inner;
  const outer = normalized.outer;
  const h = Math.max(0, trimHmm - top - bottom);

  if (!isSpread) {
    const w = Math.max(0, trimWmm - outer - outer);
    return [{ x: outer, y: top, w, h }];
  }

  const left = {
    x: outer,
    y: top,
    w: Math.max(0, trimWmm - outer - inner),
    h
  };

  const right = {
    x: trimWmm + inner,
    y: top,
    w: Math.max(0, trimWmm - inner - outer),
    h
  };

  return [left, right];
}

export function isBookSpreadsDoc(doc: any) {
  return String(doc?.meta?.layout_mode || "pages") === "book_spreads";
}

export function countUnitPhysicalPages(unit: any) {
  return String(unit?.kind || "single") === "spread" ? 2 : 1;
}

export function physicalPageCountFromContentDoc(doc: any) {
  const units = Array.isArray(doc?.pages) ? doc.pages : [];
  if (!isBookSpreadsDoc(doc)) return units.length;
  return units.reduce((sum: number, unit: any) => sum + countUnitPhysicalPages(unit), 0);
}

export function createDefaultContentDataFromProduct(prod: any) {
  const layout = normalizeContentLayout(prod?.content_layout);
  const minPhysical = Math.max(2, Number(prod?.min_pages ?? 24));

  const pages: any[] = [];
  if (layout === "book") {
    pages.push({ kind: "single", elements: [] });
    pages.push({ kind: "single", elements: [] });
  } else {
    pages.push({ kind: "spread", elements: [] });
  }

  const base = {
    meta: {
      format_key: prod?.format_key,
      bleed_mm: Number(prod?.bleed_mm ?? 3),
      safe_mm: Number(prod?.safe_mm ?? 5),
      layout_mode: "book_spreads",
      content_layout: layout,
      page_margins_mm: normalizePageMarginsMm(prod?.page_margins_mm),
    },
    pages,
  };

  return ensureMinPhysicalPages(base, minPhysical);
}

export function ensureMinPhysicalPages(doc: any, minPhysical: number) {
  const layout = normalizeContentLayout(doc?.meta?.content_layout);
  const pages = Array.isArray(doc?.pages) ? [...doc.pages] : [];

  if (layout === "book") {
    if (pages.length < 2) {
      pages.length = 0;
      pages.push({ kind: "single", elements: [] });
      pages.push({ kind: "single", elements: [] });
    }
  } else if (pages.length < 1) {
    pages.length = 0;
    pages.push({ kind: "spread", elements: [] });
  }

  let physicalCount = physicalPageCountFromContentDoc({
    ...doc,
    meta: { ...(doc?.meta || {}), layout_mode: "book_spreads", content_layout: layout },
    pages,
  });

  while (physicalCount < minPhysical) {
    if (layout === "book") pages.splice(Math.max(1, pages.length - 1), 0, { kind: "spread", elements: [] });
    else pages.push({ kind: "spread", elements: [] });

    physicalCount = physicalPageCountFromContentDoc({
      ...doc,
      meta: { ...(doc?.meta || {}), layout_mode: "book_spreads", content_layout: layout },
      pages,
    });
  }

  return {
    ...doc,
    meta: {
      ...(doc?.meta || {}),
      layout_mode: "book_spreads",
      content_layout: layout,
      page_margins_mm: normalizePageMarginsMm(doc?.meta?.page_margins_mm),
    },
    pages,
  };
}

/**
 * Convert legacy physical pages[] into book_spreads units.
 * Elements from right physical pages are shifted by +pageWmm.
 */
export function migratePagesToBookSpreadsUnits(doc: any, pageWmm: number, contentLayout: "book" | "spreads_only") {
  const physical = Array.isArray(doc?.pages) ? doc.pages : [];

  const shiftRight = (elements: any[]) =>
    (Array.isArray(elements) ? elements : []).map((el: any) => ({
      ...el,
      x_mm: Number(el?.x_mm ?? 0) + pageWmm,
    }));

  const units: any[] = [];

  if (contentLayout === "spreads_only") {
    const pages = [...physical];
    if (pages.length % 2 === 1) pages.push({ elements: [] });

    for (let i = 0; i < pages.length; i += 2) {
      const leftEls = pages[i]?.elements || [];
      const rightEls = pages[i + 1]?.elements || [];
      units.push({
        kind: "spread",
        elements: [...leftEls, ...shiftRight(rightEls)],
      });
    }
  } else {
    if (physical.length === 0) {
      units.push({ kind: "single", elements: [] });
      units.push({ kind: "single", elements: [] });
      return {
        ...doc,
        meta: {
          ...(doc?.meta || {}),
          layout_mode: "book_spreads",
          content_layout: contentLayout,
          page_margins_mm: normalizePageMarginsMm(doc?.meta?.page_margins_mm),
        },
        pages: units,
      };
    }

    const first = physical[0] || { elements: [] };
    const last = physical.length > 1 ? physical[physical.length - 1] : { elements: [] };

    units.push({ kind: "single", elements: first.elements || [] });

    const middle = physical.slice(1, -1);
    if (middle.length % 2 === 1) middle.push({ elements: [] });

    for (let i = 0; i < middle.length; i += 2) {
      const leftEls = middle[i]?.elements || [];
      const rightEls = middle[i + 1]?.elements || [];
      units.push({
        kind: "spread",
        elements: [...leftEls, ...shiftRight(rightEls)],
      });
    }

    units.push({ kind: "single", elements: last.elements || [] });
  }

  return {
    ...doc,
    meta: {
      ...(doc?.meta || {}),
      layout_mode: "book_spreads",
      content_layout: contentLayout,
      page_margins_mm: normalizePageMarginsMm(doc?.meta?.page_margins_mm),
    },
    pages: units,
  };
}

export function buildContentUnitTiles(doc: any) {
  const units = Array.isArray(doc?.pages) ? doc.pages : [];
  const tiles: Array<{ index: number; kind: "single" | "spread"; label: string; elementCount: number }> = [];
  let physicalPage = 1;

  for (let index = 0; index < units.length; index++) {
    const unit = units[index] || { elements: [] };
    const kind = String(unit?.kind || "single") === "spread" ? "spread" : "single";
    const label =
      kind === "spread" ? `Pages ${physicalPage}-${physicalPage + 1}` : `Page ${physicalPage}`;

    tiles.push({
      index,
      kind,
      label,
      elementCount: Array.isArray(unit?.elements) ? unit.elements.length : 0,
    });

    physicalPage += kind === "spread" ? 2 : 1;
  }

  return tiles;
}
